// Uang Muka Vendor — detail (#4b-3c, layar 2)
//
// Layar dengan percabangan role terbanyak di modul Purchasing: LIMA role
// melihat tombol berbeda, dan tiga di antaranya menentukan nasib uang yang
// sudah keluar.
//   ADMIN_PURCHASING : Batalkan  (selama uang belum keluar)
//   KEPALA_CABANG    : Setujui · Tolak            (gerbang approval)
//   OWNER            : sama dengan KC, + satu-satunya yang boleh
//                      MENGHAPUSBUKUKAN (mengakui kerugian)
//   FINANCE          : Catat Pembayaran · Potongkan ke Tagihan · Selesaikan
//
// NOL KONSTANTA BISNIS. Delapan status, transisi (termasuk dari status apa
// sebuah aksi boleh dijalankan), ambang panjang alasan yang BERBEDA per aksi,
// status mana yang menahan plafon, dan batas persetujuan SEMUANYA dari
// GET /purchasing/meta/dp. Tabel role di atas adalah CATATAN hasil membaca
// meta, bukan yang ditegakkan berkas ini.
//
// UI bukan keamanan — tiap aksi tetap dijaga role guard + state machine +
// CHECK database. Yang ditambahkan di sini: orang tidak menekan tombol yang
// pasti ditolak, dan tahu akibatnya sebelum menekan.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ambilSemua } from "../../lib/ambilSemua";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { BusyButton } from "../../components/etams/BusyButton";
import { VendorProofUploader } from "../../components/etams/VendorProofUploader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  ArrowLeft, RefreshCw, HandCoins, ShieldCheck, AlertTriangle, FileWarning,
  Banknote, Info, Scissors, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate, formatDateTime, todayISO } from "../../lib/format";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariPo, labelPo, ambilPo } from "../../lib/sumberPilihan";
import { useStoreTimezones } from "../../lib/timezone";
import { useAuth } from "../../contexts/AuthContext";
import { bisaAksiMeta, transisiMeta } from "./VendorInvoiceDetail";
import { dpStatusBadge, badgeUmur, hitungPlafon } from "./VendorDPs";
import UangKembaliStatus from "../../components/etams/UangKembaliStatus";
import {
  getDP, getDPMeta, listDPs, approveDP, rejectDP, cancelDP, payDP, applyDP,
  settleDP, writeOffDP, uploadDPProof, listInvoices, getPOMeta,
  apiErrorMessage,
} from "../../lib/purchasingApi";

export default function VendorDPDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tzForRegion } = useStoreTimezones();
  const { profile } = useAuth();

  const [meta, setMeta] = useState(null);
  const [dp, setDp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  //: DP lain di PO yang sama — dasar hitungan plafon yang ditampilkan.
  const [dpSaudara, setDpSaudara] = useState(null);

  //: satu dialog beralasan dipakai bertiga (reject / cancel / settle-reason
  //: sudah punya dialognya sendiri). Bentuknya identik, hanya endpoint,
  //: judul, dan AMBANG PANJANGNYA yang berbeda.
  const [alasanDlg, setAlasanDlg] = useState(null);
  const [reason, setReason] = useState("");
  const [approveOpen, setApproveOpen] = useState(false);

  // ---- Bayar (FINANCE) --------------------------------------------------
  const [bayarOpen, setBayarOpen] = useState(false);
  const [konfirmasiBayar, setKonfirmasiBayar] = useState(false);
  const [bayar, setBayar] = useState({});
  const [bukti, setBukti] = useState(null);

  // ---- Potongkan ke tagihan (FINANCE) -----------------------------------
  const [applyOpen, setApplyOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceId, setInvoiceId] = useState("");

  // ---- Selesaikan (FINANCE) ---------------------------------------------
  const [settleOpen, setSettleOpen] = useState(false);
  const [settle, setSettle] = useState({});
  const [poVendor, setPoVendor] = useState([]);
  const [poVendorError, setPoVendorError] = useState(null);
  const [poMeta, setPoMeta] = useState(null);

  // ---- Hapus buku (OWNER) -----------------------------------------------
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [woReason, setWoReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getDP(id);
      setDp(r.data);
      ambilSemua(listDPs, { po_id: r.data.po_id })
        .then(items => setDpSaudara(items))
        .catch((e) => { setDpSaudara([]); toast.error(apiErrorMessage(e, "Daftar uang muka lain PO ini gagal dimuat — muat ulang halaman.")); });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat uang muka."));
      setDp(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getDPMeta()
      .then(r => setMeta(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Gagal memuat info uang muka.")));
    // Dipakai untuk menilai PO mana yang masih bisa menampung kredit —
    // lihat catatan panjang di `poKreditLayak`.
    getPOMeta().then(r => setPoMeta(r.data)).catch((e) => { setPoMeta(null); toast.error(apiErrorMessage(e, "Data gagal dimuat.")); });
  }, []);

  const jalankan = async (fn, okMsg, failMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      setAlasanDlg(null);
      setApproveOpen(false);
      setReason("");
      setKonfirmasiBayar(false);
      setBayarOpen(false);
      setBukti(null);
      setApplyOpen(false);
      setSettleOpen(false);
      setWriteOffOpen(false);
      setWoReason("");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, failMsg));
    } finally {
      setBusy(false);
    }
  };

  const tz = tzForRegion(dp?.region_id || dp?.region?.id);

  //: Kandidat tagihan untuk potongan: milik PO yang SAMA dan berstatus yang
  //: memang menerima potongan (meta.eligibility.invoice_statuses_for_apply —
  //: PENDING_APPROVAL sengaja di luar, kalau lolos ia bisa melompatkan
  //: tagihan ke PAID tanpa pernah lewat gerbang approval).
  useEffect(() => {
    if (!applyOpen || !dp?.po_id || !meta) return;
    const layak = meta.eligibility?.invoice_statuses_for_apply || [];
    ambilSemua(listInvoices, { po_id: dp.po_id })
      .then(items => setInvoices(items.filter(
        iv => layak.includes(iv.status))))
      .catch((e) => { setInvoices([]); toast.error(apiErrorMessage(e, "Daftar invoice vendor gagal dimuat — muat ulang halaman.")); });
  }, [applyOpen, dp?.po_id, meta]);

  //: Kandidat PO tujuan kredit: vendor yang SAMA, bukan PO milik uang muka
  //: ini sendiri, dan masih bisa menampung kredit.
  //:
  //: 28 Sep 2026: PO DICARI di server (PemilihCari, vendor + `belum_selesai`) —
  //: dulu SELURUH PO vendor ditarik ke browser (PO satu vendor bertambah tiap
  //: tahun). Yang dimuat saat dialog dibuka hanya SAMPEL kecil, cukup untuk
  //: menjawab "adakah PO yang bisa menampung kredit?" sebelum orang memilih cara.
  useEffect(() => {
    if (!settleOpen || !dp?.vendor_id) return;
    cariPo("", { vendor_id: dp.vendor_id, belum_selesai: true })
      .then(items => { setPoVendor(items || []); setPoVendorError(null); })
      // Ditelan = layar bilang "tidak ada PO yang bisa menampung kredit"
      // padahal mungkin ada. Di layar ini taruhannya uang yang SUDAH keluar:
      // daftar kosong palsu mendorong orang memilih REFUND (menagih balik ke
      // vendor) padahal CREDIT_NEXT_PO lebih mudah — keputusan uang yang
      // diambil atas informasi yang salah.
      .catch(e => {
        setPoVendor([]);
        setPoVendorError(apiErrorMessage(
          e, "Gagal memuat PO vendor ini — daftar di bawah belum tentu lengkap."));
      });
  }, [settleOpen, dp?.vendor_id]);

  const layakKredit = useCallback((p) => {
    // DUA penyaring, dua sifat berbeda:
    //
    // 1. `p.id !== dp.po_id` — STRUKTURAL, tidak bisa salah. Backend
    //    menolaknya (CREDITED_PO_IS_SELF) dan constraint DB ikut menjaga,
    //    tapi menyodorkannya sebagai pilihan berarti orang menekan tombol
    //    lalu kena error atas sesuatu yang sudah bisa kita ketahui di sini.
    //    "Kredit ke PO sendiri" adalah cara memutar mengulur pagu PO.
    //
    // 2. Status yang masih bisa menampung kredit — TIDAK ada di /meta/dp,
    //    jadi diturunkan dari `tone` status PO: yang sudah `done` (Selesai /
    //    Ditutup) dan `void` (Batal) tidak menampung apa pun lagi. Sengaja
    //    BUKAN daftar kode status yang ditulis di JS. Kalau meta PO belum
    //    termuat, penyaring ini dilepas dan backend yang menolak
    //    (CREDITED_PO_NOT_OPEN) — lebih baik satu bolak-balik daripada
    //    menyembunyikan PO yang sebenarnya sah.
    const mati = new Set((poMeta?.statuses || [])
      .filter(s => s.tone === "done" || s.tone === "void")
      .map(s => s.code));
    return p.id !== dp?.po_id && (mati.size === 0 || !mati.has(p.status));
  }, [poMeta, dp?.po_id]);
  //: Sampel yang layak — hanya untuk pesan "tidak ada PO penampung kredit".
  const poKreditLayak = useMemo(() => poVendor.filter(layakKredit), [poVendor, layakKredit]);
  const cariPoKredit = useCallback(async (kata) => (dp?.vendor_id
    ? (await cariPo(kata, { vendor_id: dp.vendor_id, belum_selesai: true })).filter(layakKredit)
    : []), [dp?.vendor_id, layakKredit]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="dp-detail-loading">
        <div className="h-8 w-56 bg-muted rounded animate-pulse" />
        {[...Array(4)].map((_, i) =>
          <div key={i} className="h-12 bg-muted rounded animate-pulse" />)}
      </div>
    );
  }

  if (!dp) {
    return (
      <div className="p-12 text-center text-muted-foreground text-sm"
           data-testid="dp-detail-empty">
        Uang muka tidak ditemukan — mungkin sudah dibatalkan, atau bukan bagian
        region Anda.
        <div className="mt-3">
          <Button variant="outline" size="sm"
                  onClick={() => navigate("/purchasing/dp")}>
            Kembali ke daftar
          </Button>
        </div>
      </div>
    );
  }

  const b = dpStatusBadge(meta, dp.status);
  const umur = badgeUmur(dp);
  const plafon = dpSaudara ? hitungPlafon(meta, dp.po, dpSaudara) : null;
  const appr = meta?.approval || {};

  // Batas persetujuan dari REGION UANG MUKA INI (Q74), yang ikut terkirim
  // bersama barisnya — bukan dari meta.approval.caller_region_limit_*, yang
  // cakupannya region si PEMANGGIL. Owner dan Finance ber-region_id NULL,
  // jadi memakai angka pemanggil membuat layar ini memberi tahu Owner "batas
  // region belum ditetapkan" untuk region yang batasnya jelas terisi.
  const batasRegion = dp.region?.payment_approval_limit_idr;
  const batasDiset = batasRegion !== null && batasRegion !== undefined;
  const diAtasBatas = batasDiset && Number(dp.amount) > Number(batasRegion);
  const pemohonSendiri = appr.requester_cannot_approve
    && !!profile?.id && dp.requested_by === profile.id;

  const bolehSetujui  = bisaAksiMeta(meta, "approve", dp.status);
  const bolehTolak    = bisaAksiMeta(meta, "reject", dp.status);
  const bolehBatal    = bisaAksiMeta(meta, "cancel", dp.status);
  const bolehBayar    = bisaAksiMeta(meta, "pay", dp.status);
  const bolehPotong   = bisaAksiMeta(meta, "apply", dp.status);
  const bolehSelesai  = bisaAksiMeta(meta, "settle", dp.status);
  const bolehHapusBuku = bisaAksiMeta(meta, "write_off", dp.status);
  const adaAksi = bolehSetujui || bolehTolak || bolehBatal || bolehBayar
    || bolehPotong || bolehSelesai || bolehHapusBuku;

  const flowSteps = (meta?.statuses || []).filter(s => s.flow !== null)
    .sort((a, c) => a.flow - c.flow);
  const curFlow = (meta?.statuses || []).find(s => s.code === dp.status)?.flow ?? null;

  const minAlasan = (action) =>
    transisiMeta(meta, action)?.min_reason_length || 3;
  const alasanKurang = alasanDlg
    ? reason.trim().length < minAlasan(alasanDlg.action) : true;
  const bukaAlasan = (action, title, desc, run) => {
    setReason("");
    setAlasanDlg({ action, title, desc, run });
  };

  // ---- Bayar -----------------------------------------------------------
  const proofMeta = meta?.proof || null;
  const metodeList = meta?.payment_methods || [];
  const buktiKurang = !!proofMeta?.required && !bukti?.proof_file_id;
  const bisaKirimBayar = !!bayar.payment_method && !buktiKurang;

  const bukaDialogBayar = () => {
    setBukti(null);
    setBayar({
      payment_method: metodeList[0]?.code || "",
      bank_ref: "",
      paid_date: todayISO(),
    });
    setBayarOpen(true);
  };

  const kirimBayar = () => jalankan(
    () => payDP(dp.id, {
      payment_method: bayar.payment_method,
      proof_file_id: bukti?.proof_file_id,
      bank_ref: bayar.bank_ref?.trim() || null,
      paid_date: bayar.paid_date || null,
    }),
    "Pembayaran uang muka tercatat.",
    "Gagal mencatat pembayaran uang muka.");

  // ---- Selesaikan ------------------------------------------------------
  const tipeList = meta?.settlement_types || [];
  const tipeTerpilih = tipeList.find(t => t.code === settle.settlement_type) || null;
  const perluPO = !!tipeTerpilih?.requires_credited_po;
  const minAlasanSettle = minAlasan("settle");
  const settleAlasanKurang = (settle.reason || "").trim().length < minAlasanSettle;
  const bisaKirimSettle = !!settle.settlement_type && !settleAlasanKurang
    && (!perluPO || !!settle.credited_to_po_id);

  const bukaSettle = () => {
    setSettle({ settlement_type: "", reason: "", credited_to_po_id: "" });
    setSettleOpen(true);
  };

  const kirimSettle = () => jalankan(
    () => settleDP(dp.id, {
      settlement_type: settle.settlement_type,
      reason: settle.reason.trim(),
      // Dikirim HANYA saat cara penyelesaiannya memang menuntutnya —
      // pasangan dua arah dijaga constraint DB: REFUND wajib TIDAK punya PO
      // tujuan, CREDIT_NEXT_PO wajib punya.
      credited_to_po_id: perluPO ? settle.credited_to_po_id : null,
    }),
    "Uang muka diselesaikan.",
    "Gagal menyelesaikan uang muka.");

  // ---- Hapus buku ------------------------------------------------------
  const minAlasanWO = minAlasan("write_off");
  const woKurang = woReason.trim().length < minAlasanWO;

  return (
    <div className="space-y-6" data-testid="dp-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" data-testid="dp-detail-back"
                  onClick={() => navigate("/purchasing/dp")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2 flex-wrap">
              <HandCoins className="h-6 w-6" /> {dp.dp_number}
              <StatusBadge tone={b.tone} testId="dp-detail-status">{b.label}</StatusBadge>
              {umur && dp.is_aging && (
                <StatusBadge tone={umur.tone} testId="dp-detail-aging">
                  {umur.label}
                </StatusBadge>
              )}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{b.description}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="dp-detail-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Progres alur — nomornya dari backend (`flow`), bukan urutan tebakan.
          Status di luar alur normal (Ditolak/Batal/Kredit/Hapus buku)
          sengaja tidak punya nomor dan tidak muncul sebagai langkah. */}
      {flowSteps.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="dp-detail-flow">
          {flowSteps.map(s => {
            const lewat = curFlow !== null && s.flow <= curFlow;
            return (
              <span key={s.code} title={s.description}
                    className={`text-xs px-2 py-1 rounded border ${
                      lewat
                        ? "bg-success-soft text-success-soft-foreground border-success/40"
                        : "text-muted-foreground border-border"}`}>
                {s.flow}. {s.label}
              </span>
            );
          })}
        </div>
      )}

      <Card className="p-4">
        <h2 className="text-sm font-medium mb-3">Ringkasan</h2>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-muted-foreground text-xs">Vendor</dt>
            <dd className="font-medium">{dp.vendor?.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Purchase Order</dt>
            <dd>
              {dp.po?.id ? (
                <button type="button" data-testid="dp-detail-po-link"
                        className="font-medium underline underline-offset-2 hover:text-foreground/80"
                        onClick={() => navigate(`/purchasing/po/${dp.po.id}`)}>
                  {dp.po.po_number}
                </button>
              ) : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Wilayah</dt>
            <dd>{dp.region?.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Tanggal uang muka</dt>
            <dd>{formatDate(dp.dp_date, tz)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Nominal</dt>
            <dd className="text-lg font-semibold" data-testid="dp-detail-amount">
              {formatIDR(dp.amount)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Nilai PO</dt>
            <dd>{formatIDR(dp.po?.total_amount)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Umur</dt>
            <dd>
              {/* TIGA keadaan, bukan dua. Backend sengaja mengosongkan
                  age_days begitu DP diselesaikan -- ia bukan titipan
                  menggantung lagi. Tanpa cabang ketiga, layar memakai
                  kalimat "uang belum keluar" untuk DP yang uangnya JUSTRU
                  sudah keluar dan sudah dipotongkan. */}
              {dp.age_days === null || dp.age_days === undefined ? (
                <span className="text-muted-foreground">
                  {dp.paid_at
                    ? "Sudah diselesaikan — tidak menua lagi"
                    : "Belum dihitung (uang belum keluar)"}
                </span>
              ) : `${dp.age_days} hari sejak dibayar`}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Diajukan</dt>
            <dd>{formatDateTime(dp.requested_at || dp.created_at, tz)}</dd>
          </div>
          <div className="col-span-2 md:col-span-4">
            <dt className="text-muted-foreground text-xs">Alasan pengajuan</dt>
            <dd data-testid="dp-detail-reason">{dp.request_reason || "—"}</dd>
          </div>
          {dp.notes && (
            <div className="col-span-2 md:col-span-4">
              <dt className="text-muted-foreground text-xs">Catatan</dt>
              <dd>{dp.notes}</dd>
            </div>
          )}
        </dl>

        {/* Plafon PO — konteks yang menentukan apakah masih ada ruang. */}
        {plafon && !plafon.tidakDiketahui && (
          <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-x-6 gap-y-3 text-sm"
               data-testid="dp-detail-plafon">
            <div>
              <dt className="text-muted-foreground text-xs">
                Plafon PO ({Math.round(meta.limits.cap_ratio * 100)}%)
              </dt>
              <dd>{formatIDR(plafon.plafon)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">
                Terpakai (termasuk uang muka ini)
              </dt>
              <dd>{formatIDR(plafon.terpakai)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Sisa</dt>
              <dd className="font-semibold">
                {formatIDR(Math.max(plafon.sisa, 0))}
              </dd>
            </div>
          </div>
        )}
      </Card>

      {/* ---------------------------- Jejak -------------------------------- */}
      {(dp.approved_at || dp.rejected_at || dp.paid_at || dp.applied_at
        || dp.settled_at || dp.written_off_at || dp.cancelled_at) && (
        <Card className="p-4" data-testid="dp-detail-jejak">
          <h2 className="text-sm font-medium mb-3">Jejak</h2>
          <div className="space-y-2 text-sm">
            {dp.approved_at && (
              <div className="flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 text-success-soft-foreground shrink-0" />
                <span>
                  Disetujui{dp.approved_role_code ? ` (${dp.approved_role_code})` : ""}{" "}
                  pada {formatDateTime(dp.approved_at, tz)}
                </span>
              </div>
            )}
            {dp.rejected_at && (
              <div className="flex items-start gap-2">
                <FileWarning className="h-4 w-4 mt-0.5 text-danger-soft-foreground shrink-0" />
                <span>
                  Ditolak pada {formatDateTime(dp.rejected_at, tz)}
                  {dp.reject_reason && (
                    <span className="block text-muted-foreground">
                      Alasan: {dp.reject_reason}
                    </span>
                  )}
                </span>
              </div>
            )}
            {dp.cancelled_at && (
              <div className="flex items-start gap-2">
                <FileWarning className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <span>
                  Dibatalkan pada {formatDateTime(dp.cancelled_at, tz)}
                  {dp.cancel_reason && (
                    <span className="block text-muted-foreground">
                      Alasan: {dp.cancel_reason}
                    </span>
                  )}
                </span>
              </div>
            )}
            {dp.paid_at && (
              <div className="flex items-start gap-2">
                <Banknote className="h-4 w-4 mt-0.5 text-info-soft-foreground shrink-0" />
                <span>
                  Dibayar pada {formatDateTime(dp.paid_at, tz)}
                  {dp.payment_method ? ` · ${dp.payment_method}` : ""}
                  {dp.bank_ref ? ` · ref ${dp.bank_ref}` : ""}
                  {/* Bukti transfer (Q80). Signed URL diterbitkan backend
                      saat baris ini dibaca dan berumur pendek — jangan
                      disimpan atau disebar. Bisa dibuka semua yang berwenang
                      membaca uang muka ini, bukan cuma Finance: Owner dan KC
                      justru memerlukannya saat menagih uang muka yang
                      menggantung. */}
                  {dp.proof?.signed_url ? (
                    <span className="block">
                      <a href={dp.proof.signed_url} target="_blank"
                         rel="noopener noreferrer" data-testid="dp-detail-proof-link"
                         className="inline-flex items-center gap-1 underline underline-offset-2">
                        <ExternalLink className="h-3.5 w-3.5" /> Buka bukti transfer
                        {dp.proof.expires_in_sec
                          ? ` (tautan berlaku ${Math.round(dp.proof.expires_in_sec / 60)} menit)`
                          : ""}
                      </a>
                      {dp.proof.uploaded_at && (
                        <span className="block text-muted-foreground text-xs">
                          Diunggah {formatDateTime(dp.proof.uploaded_at, tz)}
                          {dp.proof.mime_type ? ` · ${dp.proof.mime_type}` : ""}
                        </span>
                      )}
                    </span>
                  ) : dp.proof_file_id ? (
                    <span className="block text-muted-foreground">
                      Bukti tercatat tapi tautannya gagal disiapkan. Buka ulang
                      halaman ini; kalau tetap gagal, laporkan ke Owner —
                      berkasnya mungkin benar-benar hilang dari penyimpanan.
                    </span>
                  ) : null}
                </span>
              </div>
            )}
            {dp.applied_at && (
              <div className="flex items-start gap-2">
                <Scissors className="h-4 w-4 mt-0.5 text-success-soft-foreground shrink-0" />
                <span>
                  Dipotongkan pada {formatDateTime(dp.applied_at, tz)}
                  {dp.applied_invoice?.internal_number && (
                    <>
                      {" ke tagihan "}
                      <button type="button" data-testid="dp-detail-inv-link"
                              className="underline underline-offset-2"
                              onClick={() => navigate(
                                `/purchasing/invoices/${dp.applied_invoice_id}`)}>
                        {dp.applied_invoice.internal_number}
                      </button>
                    </>
                  )}
                </span>
              </div>
            )}
            {dp.settled_at && (
              <div className="flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 text-success-soft-foreground shrink-0" />
                <span>
                  Diselesaikan pada {formatDateTime(dp.settled_at, tz)}
                  {dp.settlement_type && (
                    <>
                      {" — "}
                      {tipeList.find(t => t.code === dp.settlement_type)?.label
                        || dp.settlement_type}
                    </>
                  )}
                  {dp.credited_to_po?.po_number && (
                    <span className="block text-muted-foreground">
                      Dikreditkan ke PO {dp.credited_to_po.po_number}
                    </span>
                  )}
                  {dp.settlement_reason && (
                    <span className="block text-muted-foreground">
                      Alasan: {dp.settlement_reason}
                    </span>
                  )}
                </span>
              </div>
            )}
            <UangKembaliStatus uangKembali={dp.uang_kembali} testId="dp-uang-kembali" />
            {dp.written_off_at && (
              <div className="flex items-start gap-2 rounded border border-warning/40
                              bg-warning-soft p-2 text-warning-soft-foreground"
                   data-testid="dp-detail-writeoff">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  <strong>Dihapusbukukan sebagai kerugian</strong> pada{" "}
                  {formatDateTime(dp.written_off_at, tz)}
                  {dp.written_off_role_code ? ` oleh ${dp.written_off_role_code}` : ""}.
                  {dp.write_off_reason && (
                    <span className="block">Alasan: {dp.write_off_reason}</span>
                  )}
                  <span className="block text-xs mt-1 opacity-90">
                    Uangnya TETAP dihitung menahan plafon PO — write-off
                    mengesahkan kerugiannya, bukan menghapus uangnya.
                  </span>
                </span>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ---------------------------- Tindakan ----------------------------- */}
      {adaAksi && (
        <Card className="p-4" data-testid="dp-detail-actions">
          <h2 className="text-sm font-medium mb-3">Tindakan</h2>

          {bolehSetujui && !batasDiset && (
            <div className="mb-3 flex items-start gap-2 rounded border border-warning/40 bg-warning-soft text-warning-soft-foreground p-3 text-sm"
                 data-testid="dp-warn-limit-unset">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Batas persetujuan pembayaran untuk region{" "}
                {dp.region?.name || "ini"} <strong>belum ditetapkan</strong>.
                Itu bukan berarti tanpa batas — persetujuan bisa ditolak
                backend. Minta Owner mengisi batasnya lebih dulu.
              </span>
            </div>
          )}
          {bolehSetujui && diAtasBatas && (
            <div className="mb-3 flex items-start gap-2 rounded border border-warning/40 bg-warning-soft text-warning-soft-foreground p-3 text-sm"
                 data-testid="dp-warn-above-limit">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Nominal {formatIDR(dp.amount)} berada di atas batas persetujuan
                region {dp.region?.name || "ini"} ({formatIDR(batasRegion)}).
                Hanya {appr.above_limit_required_role || "OWNER"} yang bisa
                menyetujuinya.
              </span>
            </div>
          )}
          {/* `bolehTolak` ikut, bukan hanya `bolehSetujui`: backend memblokir
              MENOLAK pengajuan sendiri dengan error yang sama
              (`vendor_dp_service.reject_dp` -> REQUESTER_CANNOT_APPROVE).
              Spanduk yang hanya menyebut "menyetujui" membuat tombol Tolak
              tampak sebagai jalan keluar, padahal ia 403.

              ⚠ Diukur 28 Agu 2026: dengan tuple peran SEKARANG blok ini nol
              terjangkau di produksi — DP_WRITE_ROLES = (ADMIN_PURCHASING,)
              dan DP_APPROVE_ROLES = (OWNER, KEPALA_CABANG) TERPISAH TOTAL,
              jadi pemohon nol pernah jadi penyetuju. Ia menyala hanya di
              bawah penyamaran UAT.

              SENGAJA DIPERTAHANKAN: ia jadi hidup begitu satu tuple berubah
              — dan di PR, Kepala Cabang MEMANG mengajukan sekaligus
              menyetujui punya Admin Gudang. Pertahanan berlapis yang menunggu,
              bukan kode mati yang lupa dibuang. */}
          {(bolehSetujui || bolehTolak) && pemohonSendiri && (
            <div className="mb-3 flex items-start gap-2 rounded border border-danger/40 bg-danger-soft text-danger-soft-foreground p-3 text-sm"
                 data-testid="dp-warn-self-approval">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Uang muka ini Anda sendiri yang mengajukan. Pemohon tidak boleh
                menyetujui <strong>maupun menolak</strong> pengajuannya sendiri
                — mintakan ke orang lain yang berwenang.
              </span>
            </div>
          )}
          {bolehPotong && (
            <div className="mb-3 flex items-start gap-2 rounded border border-info/40 bg-info-soft text-info-soft-foreground p-3 text-sm"
                 data-testid="dp-hint-apply">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Uang muka ini sudah dibayar dan menunggu diselesaikan. Jalur
                normalnya: <strong>potongkan ke satu tagihan</strong> PO yang
                sama — dipotong penuh, tidak bisa dicicil ke beberapa tagihan.
              </span>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {bolehSetujui && (
              <BusyButton busy={busy} disabled={pemohonSendiri}
                          data-testid="dp-approve-btn"
                          onClick={() => setApproveOpen(true)}>
                Setujui
              </BusyButton>
            )}
            {bolehTolak && (
              <BusyButton variant="destructive" busy={busy} data-testid="dp-reject-btn"
                          disabled={pemohonSendiri}
                          onClick={() => bukaAlasan(
                            "reject",
                            "Tolak pengajuan uang muka",
                            `Uang muka ${dp.dp_number} senilai ${formatIDR(dp.amount)} `
                            + `untuk ${dp.vendor?.name || "vendor ini"} akan ditolak. `
                            + `Uang belum keluar, jadi tidak ada yang perlu ditarik kembali.`,
                            (r) => rejectDP(dp.id, { reason: r }))}>
                Tolak
              </BusyButton>
            )}
            {bolehBayar && (
              <BusyButton busy={busy} data-testid="dp-pay-btn"
                          onClick={bukaDialogBayar}>
                Catat Pembayaran
              </BusyButton>
            )}
            {bolehPotong && (
              <BusyButton busy={busy} data-testid="dp-apply-btn"
                          onClick={() => { setInvoiceId(""); setApplyOpen(true); }}>
                Potongkan ke Tagihan
              </BusyButton>
            )}
            {bolehSelesai && (
              <BusyButton variant="outline" busy={busy} data-testid="dp-settle-btn"
                          onClick={bukaSettle}>
                Selesaikan
              </BusyButton>
            )}
            {bolehBatal && (
              <BusyButton variant="outline" busy={busy} data-testid="dp-cancel-btn"
                          onClick={() => bukaAlasan(
                            "cancel",
                            "Batalkan uang muka",
                            `Uang muka ${dp.dp_number} senilai ${formatIDR(dp.amount)} `
                            + `akan dibatalkan. Hanya bisa selama uang belum keluar.`,
                            (r) => cancelDP(dp.id, { reason: r }))}>
                Batalkan
              </BusyButton>
            )}
            {bolehHapusBuku && (
              <BusyButton variant="destructive" busy={busy}
                          data-testid="dp-writeoff-btn"
                          onClick={() => { setWoReason(""); setWriteOffOpen(true); }}>
                Hapus Buku (Kerugian)
              </BusyButton>
            )}
          </div>
        </Card>
      )}

      {/* -------------------------- Dialog setujui ------------------------- */}
      <Dialog open={approveOpen} onOpenChange={(o) => !busy && setApproveOpen(o)}>
        <DialogContent data-testid="dp-approve-dialog">
          <DialogHeader>
            <DialogTitle>Setujui uang muka</DialogTitle>
            <DialogDescription>
              Setujui pembayaran di muka {formatIDR(dp.amount)} ke{" "}
              {dp.vendor?.name || "vendor ini"} (PO {dp.po?.po_number || "—"})?
              Persetujuan ini <strong>menahan plafon PO</strong> dan
              memasukkannya ke antrean Finance untuk dieksekusi.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={busy}
                    onClick={() => setApproveOpen(false)}>
              Batal
            </Button>
            <BusyButton busy={busy} busyText="Menyetujui..."
                        data-testid="dp-approve-confirm"
                        onClick={() => jalankan(
                          () => approveDP(dp.id),
                          "Uang muka disetujui — masuk antrean Finance.",
                          "Gagal menyetujui uang muka.")}>
              Setujui
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------- Dialog beralasan ------------------------ */}
      <Dialog open={!!alasanDlg} onOpenChange={(o) => !busy && !o && setAlasanDlg(null)}>
        <DialogContent data-testid="dp-reason-dialog">
          <DialogHeader>
            <DialogTitle>{alasanDlg?.title}</DialogTitle>
            <DialogDescription>{alasanDlg?.desc}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Alasan</label>
            <Textarea value={reason} rows={3} data-testid="dp-reason-input"
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Tuliskan alasannya supaya bisa ditelusuri." />
            <p className="text-xs text-muted-foreground">
              Minimal {alasanDlg ? minAlasan(alasanDlg.action) : 3} karakter.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={busy}
                    onClick={() => setAlasanDlg(null)}>
              Batal
            </Button>
            <BusyButton variant="destructive" busy={busy} busyText="Mengirim..."
                        disabled={alasanKurang} data-testid="dp-reason-confirm"
                        onClick={() => jalankan(
                          () => alasanDlg.run(reason.trim()),
                          "Tersimpan.", "Gagal mengirim.")}>
              Kirim
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------------- Dialog catat pembayaran -------------------- */}
      {/* Menyentuh UANG dan BERKAS sekaligus — tiga lapis di sisi UI:
          validasi sebelum kirim, konfirmasi yang menyebut nominal + vendor
          (D2), dan BusyButton di kedua tombol (D1). Penjaga sebenarnya tetap
          backend: DP_PROOF_REQUIRED, PROOF_ALREADY_USED, DP_NOT_APPROVED. */}
      <Dialog open={bayarOpen}
              onOpenChange={(o) => !busy && !konfirmasiBayar && !o && setBayarOpen(false)}>
        <DialogContent className="max-w-xl" data-testid="dp-pay-dialog">
          <DialogHeader>
            <DialogTitle>Catat pembayaran uang muka</DialogTitle>
            <DialogDescription>
              Uang dianggap SUDAH keluar saat pembayaran ini dicatat — ini
              pencatatan, bukan perintah transfer. {dp.dp_number} ·{" "}
              {dp.vendor?.name || "vendor"} · {formatIDR(dp.amount)}.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Metode</label>
                <select value={bayar.payment_method || ""} data-testid="dp-pay-method"
                        onChange={(e) => setBayar(f => ({ ...f, payment_method: e.target.value }))}
                        className="mt-1 h-9 w-full rounded border bg-background px-2 text-sm">
                  {metodeList.length === 0 && <option value="">Memuat...</option>}
                  {metodeList.map(m =>
                    <option key={m.code} value={m.code}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Tanggal bayar</label>
                <Input type="date" value={bayar.paid_date || ""}
                       data-testid="dp-pay-date"
                       onChange={(e) => setBayar(f => ({ ...f, paid_date: e.target.value }))} />
                <p className="text-xs text-muted-foreground mt-1">
                  Umur uang muka dihitung dari tanggal ini.
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Referensi bank</label>
              <Input value={bayar.bank_ref || ""} data-testid="dp-pay-bankref"
                     placeholder="Nomor referensi transfer (opsional)"
                     onChange={(e) => setBayar(f => ({ ...f, bank_ref: e.target.value }))} />
            </div>

            {/* Komponen yang sama dengan pembayaran tagihan (#4b-3b) —
                pemanggil yang menentukan CARA mengunggah lewat `uploadFn`,
                komponennya tidak tahu-menahu soal invoice maupun uang muka. */}
            <VendorProofUploader
              proofMeta={proofMeta}
              disabled={busy}
              testIdPrefix="dp-pay-proof"
              uploadFn={(file) => uploadDPProof(dp.id, file).then(r => r.data)}
              onUploaded={setBukti}
            />
            {buktiKurang && (
              <p className="text-xs text-muted-foreground" data-testid="dp-pay-proof-hint">
                Unggah bukti transfernya lebih dulu — tombol lanjut baru aktif
                setelah berkasnya tersimpan.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" disabled={busy}
                    onClick={() => setBayarOpen(false)}>
              Batal
            </Button>
            <BusyButton busy={busy} disabled={!bisaKirimBayar}
                        data-testid="dp-pay-next"
                        onClick={() => setKonfirmasiBayar(true)}>
              Lanjut
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={konfirmasiBayar} onOpenChange={(o) => !busy && setKonfirmasiBayar(o)}>
        <DialogContent data-testid="dp-pay-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Catat pembayaran {formatIDR(dp.amount)}?</DialogTitle>
            <DialogDescription>
              Uang muka <strong>{formatIDR(dp.amount)}</strong> ke{" "}
              <strong>{dp.vendor?.name || "vendor ini"}</strong> (PO{" "}
              {dp.po?.po_number || "—"}) akan dicatat sebagai uang yang sudah
              keluar, lengkap dengan bukti transfernya. Uang muka yang sudah
              dibayar <strong>tidak bisa dibatalkan</strong> — penyelesaiannya
              lewat potongan ke tagihan, ditagih balik, atau dihapusbukukan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={busy}
                    onClick={() => setKonfirmasiBayar(false)}>
              Periksa lagi
            </Button>
            <BusyButton busy={busy} busyText="Menyimpan..."
                        data-testid="dp-pay-confirm" onClick={kirimBayar}>
              Catat Pembayaran
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------------------- Dialog potongkan ke tagihan ------------------ */}
      <Dialog open={applyOpen} onOpenChange={(o) => !busy && setApplyOpen(o)}>
        <DialogContent data-testid="dp-apply-dialog">
          <DialogHeader>
            <DialogTitle>Potongkan uang muka ke tagihan</DialogTitle>
            <DialogDescription>
              {formatIDR(dp.amount)} akan dipotongkan PENUH ke satu tagihan PO{" "}
              {dp.po?.po_number || "—"}. Satu uang muka untuk satu tagihan —
              tidak bisa dicicil, dan nilainya tidak boleh melebihi sisa
              tagihan tujuan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Tagihan tujuan</label>
            <select value={invoiceId} data-testid="dp-apply-invoice"
                    onChange={(e) => setInvoiceId(e.target.value)}
                    className="h-9 w-full rounded border bg-background px-2 text-sm">
              <option value="">— pilih tagihan —</option>
              {invoices.map(iv => (
                <option key={iv.id} value={iv.id}>
                  {iv.internal_number} · {formatIDR(iv.total_amount)}
                </option>
              ))}
            </select>
            {invoices.length === 0 && (
              <p className="text-xs text-muted-foreground" data-testid="dp-apply-empty">
                Tidak ada tagihan PO ini yang bisa menerima potongan. Tagihan
                harus sudah lewat gerbang persetujuan dan belum lunas
                {meta?.eligibility?.invoice_statuses_for_apply
                  ? ` (${meta.eligibility.invoice_statuses_for_apply.join(" / ")})`
                  : ""}.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={busy}
                    onClick={() => setApplyOpen(false)}>
              Batal
            </Button>
            <BusyButton busy={busy} busyText="Memotongkan..." disabled={!invoiceId}
                        data-testid="dp-apply-confirm"
                        onClick={() => jalankan(
                          () => applyDP(dp.id, { invoice_id: invoiceId }),
                          "Uang muka dipotongkan ke tagihan.",
                          "Gagal memotongkan uang muka.")}>
              Potongkan
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------- Dialog selesaikan ------------------------ */}
      <Dialog open={settleOpen} onOpenChange={(o) => !busy && setSettleOpen(o)}>
        <DialogContent className="max-w-xl" data-testid="dp-settle-dialog">
          <DialogHeader>
            <DialogTitle>Selesaikan uang muka</DialogTitle>
            <DialogDescription>
              Dipakai kalau uang muka {formatIDR(dp.amount)} ini TIDAK jadi
              dipotongkan ke tagihan PO {dp.po?.po_number || "—"}. Dua caranya
              sama-sama berakhir di status yang sama; yang membedakan ke mana
              uangnya pergi.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <div>
              <label className="text-sm font-medium">Cara penyelesaian</label>
              <select value={settle.settlement_type || ""}
                      data-testid="dp-settle-type"
                      onChange={(e) => setSettle(s => ({
                        ...s,
                        settlement_type: e.target.value,
                        // PO tujuan dibersihkan begitu caranya berganti —
                        // pasangan dua arahnya dijaga constraint DB (REFUND
                        // wajib TIDAK punya PO tujuan), jadi sisa pilihan
                        // lama akan ditolak database, bukan diabaikan.
                        credited_to_po_id: "",
                      }))}
                      className="mt-1 h-9 w-full rounded border bg-background px-2 text-sm">
                <option value="">— pilih cara —</option>
                {tipeList.map(t =>
                  <option key={t.code} value={t.code}>{t.label}</option>)}
              </select>
            </div>

            {perluPO && (
              <div>
                <label className="text-sm font-medium">PO tujuan kredit</label>
                <div className="mt-1">
                  <PemilihCari value={settle.credited_to_po_id || null} ambilSatu={ambilPo}
                               onChange={(po) => setSettle(s => ({
                                 ...s, credited_to_po_id: po?.id || "" }))}
                               cari={cariPoKredit} label={labelPo}
                               keterangan={(po) => formatIDR(po?.total_amount)}
                               tampilAwal placeholder="— pilih PO —"
                               placeholderCari="Ketik nomor PO…"
                               testId="dp-settle-po" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Hanya PO vendor yang sama dan masih berjalan. PO{" "}
                  {dp.po?.po_number || "asal"} sendiri sengaja tidak ada di
                  daftar — mengkreditkan uang muka ke PO-nya sendiri adalah
                  cara memutar mengulur plafon PO itu.
                </p>
                {poVendorError ? (
                  <p className="text-xs text-danger-soft-foreground mt-1"
                     data-testid="dp-settle-po-error">{poVendorError}</p>
                ) : (poKreditLayak.length === 0 && (
                  <p className="text-xs text-danger-soft-foreground mt-1"
                     data-testid="dp-settle-po-empty">
                    Tidak ada PO lain milik {dp.vendor?.name || "vendor ini"}{" "}
                    yang bisa menampung kredit. Pilih cara yang lain, atau
                    terbitkan PO baru lebih dulu.
                  </p>
                ))}
              </div>
            )}

            <div>
              <label className="text-sm font-medium">Alasan</label>
              <Textarea value={settle.reason || ""} rows={3}
                        data-testid="dp-settle-reason"
                        onChange={(e) => setSettle(s => ({ ...s, reason: e.target.value }))} />
              <p className="text-xs text-muted-foreground mt-1">
                Minimal {minAlasanSettle} karakter.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" disabled={busy}
                    onClick={() => setSettleOpen(false)}>
              Batal
            </Button>
            <BusyButton busy={busy} busyText="Menyimpan..." disabled={!bisaKirimSettle}
                        data-testid="dp-settle-confirm" onClick={kirimSettle}>
              Selesaikan
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------------------------- Dialog hapus buku ----------------------- */}
      {/* AKSI PALING BERAT DI LAYAR INI: mengakui uang yang sudah keluar
          sebagai KERUGIAN. Hak Owner saja, ditegakkan tuple router + service
          + CHECK database sekaligus. Dialognya WAJIB menyebut nominal,
          vendor, dan kata "kerugian" — D2 menuntut konfirmasi menyebutkan apa
          persisnya yang akan terjadi, dan di sini yang terjadi adalah uang
          dinyatakan hilang. */}
      <Dialog open={writeOffOpen} onOpenChange={(o) => !busy && setWriteOffOpen(o)}>
        <DialogContent data-testid="dp-writeoff-dialog">
          <DialogHeader>
            <DialogTitle>Hapus buku uang muka sebagai kerugian?</DialogTitle>
            <DialogDescription>
              Uang muka <strong>{formatIDR(dp.amount)}</strong> ke{" "}
              <strong>{dp.vendor?.name || "vendor ini"}</strong> (PO{" "}
              {dp.po?.po_number || "—"}) akan dinyatakan sebagai{" "}
              <strong>kerugian</strong> — uang yang sudah keluar dan tidak akan
              kembali. Ini catatan permanen dan tidak bisa dibatalkan.
              <span className="block mt-2">
                Uangnya <strong>tetap dihitung menahan plafon PO</strong>:
                penghapusbukuan mengesahkan kerugiannya, bukan menghapus
                uangnya. Kalau PO-nya masih harus dipenuhi, batalkan PO lalu
                terbitkan PO baru — jangan melonggarkan plafon PO lama.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Alasan</label>
            <Textarea value={woReason} rows={3} data-testid="dp-writeoff-reason"
                      placeholder="Kenapa uang ini dinyatakan tidak kembali? Ini akan dibaca auditor."
                      onChange={(e) => setWoReason(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              Minimal {minAlasanWO} karakter — lebih ketat daripada alasan
              lain, karena ini catatan permanen atas uang yang hilang.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={busy}
                    onClick={() => setWriteOffOpen(false)}>
              Batal
            </Button>
            <BusyButton variant="destructive" busy={busy}
                        busyText="Menghapusbukukan..." disabled={woKurang}
                        data-testid="dp-writeoff-confirm"
                        onClick={() => jalankan(
                          () => writeOffDP(dp.id, { reason: woReason.trim() }),
                          "Uang muka dihapusbukukan sebagai kerugian.",
                          "Gagal menghapusbukukan uang muka.")}>
              Hapus Buku
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
